import React from 'react';
import { Loader2, Save } from 'lucide-react';
import { Modal } from '../../../../../../../components/ui/Modal';
import type { Perfil } from '../../../../types';

interface VideoItem {
  filename: string;
  url: string;
  path: string;
}

interface VincularVideoModalProps {
  open: boolean;
  onClose: () => void;
  selectedVideo: VideoItem | null;
  vincularForm: { perfilId: string; topicoId: string; aulaId: string };
  setVincularForm: React.Dispatch<React.SetStateAction<{ perfilId: string; topicoId: string; aulaId: string }>>;
  catalog: Perfil[];
  vincularLoading: boolean;
  onConfirm: () => Promise<void>;
  getPerfilLabel: (p: Perfil) => string;
}

export function VincularVideoModal({
  open,
  onClose,
  selectedVideo,
  vincularForm,
  setVincularForm,
  catalog,
  vincularLoading,
  onConfirm,
  getPerfilLabel,
}: VincularVideoModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Vincular Vídeo Gravado"
      size="lg"
    >
      {selectedVideo && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Lado Esquerdo: Preview */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
              Visualização do Vídeo
            </label>
            <div style={{ background: '#000', borderRadius: 8, overflow: 'hidden', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <video
                src={selectedVideo.url}
                controls
                style={{ width: '100%', maxHeight: '100%' }}
              />
            </div>
            <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
              Caminho: {selectedVideo.path}
            </div>
          </div>

          {/* Lado Direito: Seletores */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
              Selecione o Destino no Catálogo
            </label>
            
            <div>
              <label style={{ display: 'block', fontSize: '.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Perfil</label>
              <select
                value={vincularForm.perfilId}
                onChange={(e) => {
                  const newPerfilId = e.target.value;
                  const perf = catalog.find(p => p.id === newPerfilId);
                  const topicos = perf?.topicos || [];
                  const firstTopico = topicos[0];
                  const aulas = firstTopico?.subtopicos || [];
                  setVincularForm({
                    perfilId: newPerfilId,
                    topicoId: firstTopico?.id || '',
                    aulaId: aulas[0]?.id || ''
                  });
                }}
                className="ecad-select-premium"
              >
                <option value="">Selecione um Perfil...</option>
                {catalog.map(p => (
                  <option key={p.id} value={p.id}>{getPerfilLabel(p)}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Módulo</label>
              <select
                value={vincularForm.topicoId}
                onChange={(e) => {
                  const newTopicoId = e.target.value;
                  const perf = catalog.find(p => p.id === vincularForm.perfilId);
                  const topico = perf?.topicos?.find(t => t.id === newTopicoId);
                  const aulas = topico?.subtopicos || [];
                  setVincularForm(prev => ({
                    ...prev,
                    topicoId: newTopicoId,
                    aulaId: aulas[0]?.id || ''
                  }));
                }}
                className="ecad-select-premium"
                disabled={!vincularForm.perfilId}
              >
                <option value="">Selecione um Módulo...</option>
                {catalog.find(p => p.id === vincularForm.perfilId)?.topicos?.map(t => (
                  <option key={t.id} value={t.id}>{t.titulo}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Aula / Roteiro</label>
              <select
                value={vincularForm.aulaId}
                onChange={(e) => setVincularForm(prev => ({ ...prev, aulaId: e.target.value }))}
                className="ecad-select-premium"
                disabled={!vincularForm.topicoId}
              >
                <option value="">Selecione uma Aula...</option>
                {catalog
                  .find(p => p.id === vincularForm.perfilId)
                  ?.topicos?.find(t => t.id === vincularForm.topicoId)
                  ?.subtopicos?.filter(s => s.status !== 'REMOVIDO')
                  ?.map(s => (
                    <option key={s.id} value={s.id}>{s.titulo}</option>
                  ))}
              </select>
            </div>

            <div style={{ flex: 1 }} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button type="button" onClick={onClose} className="btn-cancel" style={{ height: 36, padding: '0 16px', fontSize: '.8rem', borderRadius: 8 }}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={vincularLoading || !vincularForm.aulaId}
                className="btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', fontSize: '.8rem', borderRadius: 8 }}
              >
                {vincularLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                <span>Confirmar Vínculo</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
